import {
  cachedColorForCategory,
  cachedLabelForCategory,
} from '../planning/categoryCache';
import i18n, { getDateLocale } from '../../i18n';

export const MAX_VISIBLE_EVENTS = 3;

export const EVENT_CATEGORIES = ['HO', 'HNO', 'Astreinte', 'Congés', 'Formation', 'Absence'];

export const CATEGORY_LABELS = {
  HO: 'Heures Ouvrées',
  HNO: 'Heures Non Ouvrées',
  Astreinte: 'Astreinte',
  Congés: 'Congés',
  Formation: 'Formation',
  Absence: 'Absence',
};

export const CATEGORY_COLORS = {
  HO: 'blue',
  HNO: 'primary',
  Astreinte: 'amber',
  Congés: 'green',
  Formation: 'yellow',
  Absence: 'rose',
};

function getWeekdayHeaders() {
  const monday = new Date(2024, 0, 1);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toLocaleDateString(getDateLocale(), { weekday: 'short' });
  });
}

export function toIsoDateLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Monday 00:00 local for the week containing `date`. */
export function startOfWeekMonday(date = new Date()) {
  const d = startOfDay(date);
  const dow = d.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return d;
}

export function addMonths(date, delta) {
  const d = new Date(date.getFullYear(), date.getMonth() + delta, 1);
  return d;
}

export function startOfMonth(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function formatMonthTitle(date) {
  return date.toLocaleDateString(getDateLocale(), { month: 'long', year: 'numeric' });
}

export function getCurrentMonthLabel() {
  return formatMonthTitle(startOfMonth());
}

export function formatDateLabel(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(getDateLocale(), {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function colorForCategory(category) {
  return cachedColorForCategory(category, CATEGORY_COLORS[category] || 'blue');
}

export function labelForCategory(category) {
  return cachedLabelForCategory(category, CATEGORY_LABELS[category] || category);
}

export function eventDisplayTitle(event) {
  const title = String(event?.title || '').trim();
  if (title) return title;
  return event?.category ? labelForCategory(event.category) : i18n.t('planning:untitledEvent');
}

export function timeToMinutes(value) {
  const [h, m] = String(value || '00:00').split(':').map(Number);
  return h * 60 + m;
}

export function minutesToTimeValue(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function getNowCreateSlot() {
  const now = new Date();
  let start = Math.floor((now.getHours() * 60 + now.getMinutes()) / 30) * 30;
  start = Math.max(0, Math.min(start, 23 * 60));
  let end = Math.min(start + 60, 24 * 60);
  if (end <= start) end = start + 30;
  return {
    start: minutesToTimeValue(start),
    end: minutesToTimeValue(end),
  };
}

/**
 * Build a 6-week Monday-start month grid.
 * @returns {{ headers: string[], cells: Array<{ iso: string, dayNum: number, outside: boolean, today: boolean, weekend: boolean }> }}
 */
export function buildMonthGrid(monthDate = startOfMonth()) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const gridStart = startOfWeekMonday(first);
  const todayIso = toIsoDateLocal(new Date());
  const cells = [];

  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const iso = toIsoDateLocal(d);
    const dow = d.getDay();
    cells.push({
      iso,
      dayNum: d.getDate(),
      outside: d.getMonth() !== month,
      today: iso === todayIso,
      weekend: dow === 0 || dow === 6,
    });
  }

  return { headers: getWeekdayHeaders(), cells };
}

/** Seed events relative to the viewed month (day-of-month from the HTML demo). */
export function buildSeedEventsForMonth(monthDate = startOfMonth()) {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  const templates = [
    { day: 2, title: 'Morning Standup', start: '09:00', end: '10:00', category: 'HO' },
    { day: 2, title: 'Quick Sync', start: '10:30', end: '11:00', category: 'HO' },
    { day: 4, title: 'Deep Work', start: '14:00', end: '17:00', category: 'Astreinte' },
    { day: 5, title: 'Lunch with Team', start: '12:00', end: '13:00', category: 'Congés' },
    { day: 10, title: 'Morning Standup', start: '09:00', end: '10:00', category: 'HO' },
    { day: 10, title: 'Quick Sync', start: '10:30', end: '11:00', category: 'HO' },
    { day: 10, title: 'Lunch with Team', start: '12:00', end: '13:00', category: 'Congés' },
    { day: 10, title: 'Deep Work', start: '14:00', end: '17:00', category: 'Astreinte' },
    { day: 10, title: 'Design Review', start: '17:00', end: '18:00', category: 'Formation' },
    { day: 10, title: 'Client Call', start: '18:30', end: '19:00', category: 'HNO' },
    { day: 12, title: 'Morning Standup', start: '09:00', end: '10:00', category: 'HO' },
    { day: 12, title: 'Deep Work', start: '14:00', end: '17:00', category: 'Astreinte' },
  ];

  return templates
    .filter((t) => t.day <= daysInMonth)
    .map((t, i) => ({
      id: `month-seed-${y}-${m + 1}-${i}`,
      date: toIsoDateLocal(new Date(y, m, t.day)),
      title: t.title,
      start: t.start,
      end: t.end,
      category: t.category,
    }));
}
